"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";
import { api } from "~/trpc/react";

export type AvailabilityBatchSummary = {
  firstAvailableDay: Date | null;
  spotsAvailableThisWeek?: number;
};

type AvailabilityBatchContextValue = {
  isLoading: boolean;
  hasFailed: boolean;
  summaries: Record<string, AvailabilityBatchSummary>;
};

// Mirrors the resolver's Zod cap so chunked queries always validate.
const BATCH_CHUNK_SIZE = 50;

const AvailabilityBatchContext =
  createContext<AvailabilityBatchContextValue | null>(null);

export function useAvailabilityFromBatch(slug: string): {
  summary: AvailabilityBatchSummary | undefined;
  isLoading: boolean;
  hasProvider: boolean;
  hasFailed: boolean;
} {
  const ctx = useContext(AvailabilityBatchContext);
  if (!ctx) {
    return {
      summary: undefined,
      isLoading: false,
      hasProvider: false,
      hasFailed: false,
    };
  }
  return {
    summary: ctx.summaries[slug],
    isLoading: ctx.isLoading,
    hasProvider: true,
    hasFailed: ctx.hasFailed,
  };
}

interface AvailabilityBatchProviderProps {
  therapistSlugs: string[];
  includeWeeklySpots?: boolean;
  children: ReactNode;
}

export function AvailabilityBatchProvider({
  therapistSlugs,
  includeWeeklySpots = false,
  children,
}: AvailabilityBatchProviderProps) {
  // Stabilise the slug list so identical content does not retrigger the query.
  const stableSlugs = useMemo(
    () => [...therapistSlugs].sort(),
    [therapistSlugs],
  );

  // Chunk slug list to stay under the resolver's per-call cap. Lists below the
  // cap fire a single query; larger lists fan out and are merged in the value.
  const chunks = useMemo(() => {
    const out: string[][] = [];
    for (let i = 0; i < stableSlugs.length; i += BATCH_CHUNK_SIZE) {
      out.push(stableSlugs.slice(i, i + BATCH_CHUNK_SIZE));
    }
    return out;
  }, [stableSlugs]);

  const queries = api.useQueries((t) =>
    chunks.map((chunk) =>
      t.calendar.getAvailabilitySummaryBatch(
        { therapistSlugs: chunk, includeWeeklySpots },
        { staleTime: 1000 * 60 * 60 },
      ),
    ),
  );

  const value = useMemo<AvailabilityBatchContextValue>(() => {
    const summaries: Record<string, AvailabilityBatchSummary> = {};
    let isLoading = false;
    let hasFailed = false;
    let hasAnyData = false;

    for (const q of queries) {
      if (q.isPending) isLoading = true;
      if (q.isError) hasFailed = true;
      if (q.data) {
        hasAnyData = true;
        for (const [slug, summary] of Object.entries(q.data)) {
          summaries[slug] = summary;
        }
      }
    }

    return {
      isLoading: stableSlugs.length > 0 && isLoading,
      // Only declare full failure when no chunk produced data. Partial success
      // (one chunk OK, another failed) still lets teasers fall back per slug.
      hasFailed: hasFailed && !hasAnyData,
      summaries,
    };
  }, [queries, stableSlugs.length]);

  return (
    <AvailabilityBatchContext.Provider value={value}>
      {children}
    </AvailabilityBatchContext.Provider>
  );
}

// Exported for tests only.
export const AvailabilityBatchContextForTests = AvailabilityBatchContext;
